import dayjs, { Dayjs } from 'dayjs';

export type TAGS = 'Alert' | 'Production' | 'Followup' | 'Monitor';
export type URGENCY = 'immediate' | 'today' | 'week' | 'month' | 'none';
export type VALUE = 'High' | 'Medium' | 'Low' | 'None'; 
export type COMPLIANCE = 'Compliant' | 'Non-Compliant' | 'Overdue';

export interface ChecklistItem {
  tag: TAGS;
  area: string;
  insight: string;
  fee: number;
  urgency?: URGENCY;
  tooth?: string;
  category?: string;
  subCategory?: string;
}

export interface DentalChecklistItem extends ChecklistItem {
  value?: VALUE;
  colorCode?: string;
}

export interface CurrentAppointment {
  type?: string;
  time?: string; 
}

export interface RecallProfile {
  frequency?: string;
  last_visit_date?: string; 
  next_due_date?: string;
  compliance_status?: COMPLIANCE;
  reason_for_interval?: string | null;
}

export interface TodayAppointmentConsiderations {
  summary?: string[];
}

export interface DentalCoPilotAppointmentData extends AppointmentData {
  patientCareProfile?: string;
  currentAppointment?: CurrentAppointment;
  recallProfile?: RecallProfile;
  todayApptConsiderations?: TodayAppointmentConsiderations;
  socialHistoryRapport?: string[];
  checklist: DentalChecklistItem[];
}


export interface AppointmentData {
  appointmentId: string;
  operatory: string;
  appointmentDate: string;
  appointmentTime: string;
  appointmentLength: string;
  patientId: string;
  patientName: string;
  status: string;
  providerId: string;
  checklist: ChecklistItem[];
  SOAP_Evidence: any[];
  fee: number;
  description?: string;
}

export interface OperatoryData {
  operatory: string;
  appointments: DentalCoPilotAppointmentData[];
}

export interface GPSMetadata {
  date: string;
  totalOperatories: number;
  totalAppointments: number;
  totalPatients: number;
  totalItems: number;
}

export interface DisciplineData {
  [key: string]: string[];
}

export interface GPSDataResponse {
  operatories: OperatoryData[];
  metadata: GPSMetadata;
  providers: string[];
  narrative: DailyNarrativeBriefing;
  dailyBriefing: DailyBriefingData;
  disciplines: DisciplineData;
  msg?: string;
}

export interface GPSRangeDataResponse {
  startDate: string;
  endDate: string;
  days: GPSDataResponse[];
}

// State interface
interface GPSState {
  selectedDate: string;
  data: GPSDataResponse | null;
  rangeData: GPSRangeDataResponse | null;
  appliedStartDate: string;
  appliedEndDate: string;
  activeRequestId: string | null;
  loading: boolean;
  error: string | null;
  lastFetched: string | null;
  msg: string | null;
}

// Initial state
export const initialState: GPSState = {
  selectedDate: dayjs().format('YYYY-MM-DD'),
  data: null,
  rangeData: null,
  appliedStartDate: dayjs().format('YYYY-MM-DD'),
  appliedEndDate: dayjs().format('YYYY-MM-DD'),
  activeRequestId: null,
  loading: false,
  error: null,
  lastFetched: null,
  msg: null
};

export interface BriefingInsight {
  type: 'alert' | 'opportunity' | 'efficiency' | 'surprise';
  title: string;
  description: string;
  count?: number;
}

export interface Achievement {
  title: string;
  description: string;
  icon: string;
}

interface KeyMetric {
  label: string;
  value: string;
  trend?: 'up' | 'down' | 'stable';
}

export interface DailyBriefingData {
  date: string;
  teaser: string;
  insights: BriefingInsight[];
  achievements: Achievement[];
  // // TODO
  streak?: number;
  keyMetrics?: KeyMetric[];
}

export interface DailyNarrativeBriefing {
  executive_summary: string;
  critical_priorities: string[];
  production_intelligence: string[];
  operational_excellence: string[];
  strategic_recommendations: string[];
  closing_directive: string;
}
